import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import ReviewForm from "./ReviewForm";
import { API } from "@/constants/config";
import { useLang } from "@/constants/i18n";

export const Testimonials = () => {
    const { t } = useLang();
    const [approved, setApproved] = useState([]);

    useEffect(() => {
        axios
            .get(`${API}/reviews/approved`)
            .then((res) => setApproved(res.data))
            .catch(() => {});
    }, []);

    const isReal = approved.length > 0;
    const items = isReal
        ? approved.map((r) => ({
              quote: r.resena,
              name: r.nombre,
              location: r.ubicacion,
              rating: r.rating,
          }))
        : t.testimonials.items.map((item) => ({ ...item, rating: 5 }));

    return (
        <section
            id="testimonios"
            data-testid="testimonials-section"
            className="bg-tan/15 py-24 md:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10">
                <SectionHeading
                    number="04"
                    eyebrow={t.testimonials.eyebrow}
                    title={t.testimonials.title}
                />
                <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
                    {items.slice(0, 6).map((item, i) => (
                        <Reveal key={`${item.name}-${i}`} delay={0.1 + (i % 3) * 0.12}>
                            <figure
                                data-testid={`testimonial-card-${i}`}
                                className="flex h-full flex-col border border-gold/25 bg-ivory p-8 transition-transform duration-500 hover:-translate-y-1"
                            >
                                <Quote className="h-6 w-6 text-gold" />
                                <div className="mt-4 flex gap-1">
                                    {Array.from({ length: 5 }).map((_, s) => (
                                        <Star
                                            key={s}
                                            className={`h-4 w-4 ${
                                                s < item.rating
                                                    ? "fill-gold text-gold"
                                                    : "text-ink/15"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-ink/85">
                                    “{item.quote}”
                                </blockquote>
                                <figcaption className="mt-6 border-t border-gold/25 pt-4">
                                    <p className="text-sm font-medium tracking-wide text-ink">{item.name}</p>
                                    {item.location && (
                                        <p className="text-xs uppercase tracking-[0.25em] text-ink/50">
                                            {item.location}
                                        </p>
                                    )}
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
                {!isReal && (
                    <Reveal delay={0.4}>
                        <p className="mt-10 text-xs text-ink/45">{t.testimonials.fine}</p>
                    </Reveal>
                )}

                <Reveal delay={0.2}>
                    <div
                        data-testid="leave-review-block"
                        className="mt-20 grid grid-cols-1 gap-12 border border-gold/25 bg-ivory p-8 md:p-12 lg:grid-cols-2"
                    >
                        <div className="flex flex-col justify-center">
                            <p className="text-xs uppercase tracking-[0.35em] text-burgundy">
                                {t.review.ctaEyebrow}
                            </p>
                            <h3 className="mt-4 font-serif text-3xl font-light leading-tight text-ink md:text-4xl">
                                {t.review.ctaTitle}
                            </h3>
                            <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink/65 md:text-base">
                                {t.review.ctaSub}
                            </p>
                            <div className="mt-8 h-px w-20 bg-gold/70" />
                        </div>
                        <ReviewForm />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Testimonials;
